import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, HardHat, Paperclip, ShieldCheck, Target } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Field, FormSheet, RefSelect, SelectField, SheetSection, StatusPill, inputClass, useDriftRefs } from '@/components/app/DriftUI';
import { FilePicker, PersonSelect } from '@/components/app/Maskinsaker';
import { DEVIATION_KIND_LABELS, DEVIATION_STATUS_LABELS, NOT_CLARIFIED, SEVERITIES, SEVERITY_LABELS, createDeviation, deviationPath, uploadDeviationAttachments } from '@/lib/driftApi';
import { formatDateShort, osloDateOf } from '@/domain/time';
import { cn } from '@/lib/utils';

/*
  Manuelle avvik – RUH (HMS-hendelser) og kvalitetsavvik (EKSEMPELDATA).
  Egen saksgang: innmeldt → under behandling → lukket (krever konklusjon) → kan gjenåpnes med bevart historikk.
  Ikke blandet med maskinsaker eller produksjonsvarsler. Kategorier er egne felt og kan være «Ikke avklart».
  Ingen melding sendes til noen. Vedlegg bruker samme godkjente demo-lagring som maskinsakene.
*/

export const today = () => osloDateOf(new Date());
export const dateText = (d) => (d ? formatDateShort(d) : '–');
export const primaryBtn = 'rounded-[10px] bg-[var(--hr-heading)] text-white shadow-none hover:bg-[var(--hr-navy)]';
export const textareaClass = 'min-h-[88px] rounded-[10px] border-[var(--hr-border)] bg-white text-[16px] shadow-none lg:text-[14px]';
export const errText = (err) => err?.message ?? 'Ukjent feil';
export const isClarified = (v) => !!v && v !== NOT_CLARIFIED;

/* ---------- Merker og piller ---------- */

/** Type: RUH (HMS) eller kvalitet – tydelig, men rolig. */
export function DeviationKindMark({ kind, className, long = false }) {
  const Icon = kind === 'ruh' ? HardHat : Target;
  const tone = kind === 'ruh' ? 'text-[var(--hr-navy)] bg-[rgba(29,40,86,0.07)]' : 'text-[var(--hr-accent-text)] bg-[rgba(168,90,0,0.08)]';
  return (
    <span className={cn('inline-flex h-5 items-center gap-1 rounded-[6px] px-1.5 text-[12px] font-semibold uppercase tracking-[0.04em]', tone, className)}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {long ? DEVIATION_KIND_LABELS[kind] : kind === 'ruh' ? 'RUH' : 'Kvalitet'}
    </span>
  );
}

/** Status for manuelle avvik: lukket grønn, under behandling navy, innmeldt nøytral – aldri grønt før saken faktisk er lukket. */
export function DeviationStatusPill({ status, ...props }) {
  const tone = status === 'lukket' ? 'green' : status === 'under_behandling' ? 'navy' : 'neutral';
  return (
    <StatusPill tone={tone} {...props}>
      {DEVIATION_STATUS_LABELS[status] ?? status}
    </StatusPill>
  );
}

export function SeverityPill({ severity, className, ...props }) {
  const tone = severity === 'kritisk' ? 'red' : severity === 'hoy' ? 'amber' : 'neutral';
  return (
    <StatusPill tone={tone} className={className} {...props}>
      {SEVERITY_LABELS[severity] ?? severity}
    </StatusPill>
  );
}

/** Flaggene på et avvik: status, alvorlighet (høy/kritisk), frist passert, forfalte tiltak, vedlegg. */
export function DeviationBadges({ d, compact = false, className }) {
  return (
    <span className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <DeviationStatusPill status={d.status} data-testid={`dev-status-${d.id}`} />
      {(d.severity === 'hoy' || d.severity === 'kritisk') && <SeverityPill severity={d.severity} data-testid={`dev-severity-${d.id}`} />}
      {d.overdue && (
        <StatusPill tone="amber" data-testid={`dev-overdue-${d.id}`}>
          Frist passert
        </StatusPill>
      )}
      {d.actionsOverdue > 0 && (
        <StatusPill tone="amber" data-testid={`dev-actions-overdue-${d.id}`}>
          {d.actionsOverdue} {d.actionsOverdue === 1 ? 'forfalt tiltak' : 'forfalte tiltak'}
        </StatusPill>
      )}
      {!compact && d.attachments?.length > 0 && (
        <span className="inline-flex items-center gap-1 text-[12px] text-[var(--hr-muted)]" title={`${d.attachments.length} vedlegg`}>
          <Paperclip className="h-3 w-3" aria-hidden="true" />
          {d.attachments.length}
        </span>
      )}
    </span>
  );
}

/* ---------- Velgere ---------- */

/** Alvorlighet som store trykkflater – ikke nedtrekk. */
export function SeverityPicker({ value, onChange, testId = 'severity' }) {
  const tone = {
    lav: 'data-[on=true]:bg-[var(--hr-surface-2)]',
    middels: 'data-[on=true]:bg-[var(--hr-surface-2)]',
    hoy: 'data-[on=true]:border-[rgba(154,91,0,0.45)] data-[on=true]:bg-[var(--hr-amber-bg)] data-[on=true]:text-[var(--hr-amber)]',
    kritisk: 'data-[on=true]:border-[rgba(180,35,24,0.45)] data-[on=true]:bg-[var(--hr-red-bg)] data-[on=true]:text-[var(--hr-red)]',
  };
  return (
    <div className="grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Alvorlighet" data-testid={testId}>
      {SEVERITIES.map((s) => (
        <button
          key={s}
          type="button"
          role="radio"
          aria-checked={value === s}
          data-on={value === s}
          onClick={() => onChange(s)}
          className={cn('h-10 rounded-[10px] border border-[var(--hr-border)] bg-white text-[14px] font-medium text-[var(--hr-text)] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] data-[on=true]:border-[var(--hr-heading)] data-[on=true]:font-semibold data-[on=true]:text-[var(--hr-heading)]', tone[s])}
          data-testid={`${testId}-${s}`}
        >
          {SEVERITY_LABELS[s]}
        </button>
      ))}
    </div>
  );
}

/** Første valg: RUH eller kvalitetsavvik – to tydelige flater med kort forklaring. */
export function KindPicker({ value, onChange, testId = 'kind' }) {
  const kinds = [
    { id: 'ruh', label: 'RUH', sub: 'HMS-hendelse: nestenulykke, farlig forhold, personskade, materiell skade, miljø', Icon: HardHat },
    { id: 'kvalitet', label: 'Kvalitetsavvik', sub: 'Feil fraksjon, mengde, dokumentasjon, utførelse, materiell eller kundeklage', Icon: Target },
  ];
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Type avvik" data-testid={testId}>
      {kinds.map(({ id, label, sub, Icon }) => {
        const on = value === id;
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(id)}
            className={cn('flex items-start gap-3 rounded-[12px] border bg-white p-3.5 text-left transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', on ? 'border-[var(--hr-heading)] bg-[var(--hr-surface-2)]' : 'border-[var(--hr-border)]')}
            data-testid={`${testId}-${id}`}
          >
            <span className={cn('mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]', on ? 'bg-[var(--hr-heading)] text-white' : 'bg-[var(--hr-inset)] text-[var(--hr-muted)]')}>
              <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className={cn('block text-[15px] font-semibold', on ? 'text-[var(--hr-heading)]' : 'text-[var(--hr-text)]')}>{label}</span>
              <span className="block text-[13px] leading-5 text-[var(--hr-muted)]">{sub}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Kategorivalg med «Ikke avklart» først – aldri tvang til å dikte en årsak. */
export function CategorySelect({ id, value, onChange, options, testId, disabled }) {
  const sorted = useMemo(() => {
    const list = options ?? [];
    const nc = list.find((o) => o.id === NOT_CLARIFIED);
    return [...(nc ? [nc] : []), ...list.filter((o) => o.id !== NOT_CLARIFIED)];
  }, [options]);
  return <SelectField id={id} value={value || NOT_CLARIFIED} onValueChange={onChange} options={sorted.map((o) => ({ value: o.id, label: o.label }))} testId={testId} disabled={disabled} />;
}

/** Prosjektvalg: aktive prosjekter + verdien som alt står på saken; avsluttede kan hentes fram med «Vis arkiverte og avsluttede». */
export function ProjectSelect({ id, value, onChange, refs, testId }) {
  return <RefSelect id={id} value={value} onValueChange={onChange} placeholder="Velg prosjekt" list={refs?.projects} getHint={(p) => p.location} testId={testId} />;
}

export function SubprojectSelect({ id, value, onChange, refs, projectId, testId }) {
  const p = refs?.projects?.find((x) => x.id === projectId);
  const subs = (p?.subprojects ?? []).filter((s) => s.active !== false || s.id === value);
  return <SelectField id={id} value={value} onValueChange={onChange} allowEmpty emptyLabel={subs.length ? 'Hele prosjektet' : 'Ingen underprosjekter'} disabled={!p || !subs.length} options={subs.map((s) => ({ value: s.id, label: s.name }))} testId={testId} />;
}

/** Hint som alltid følger beskrivelsesfeltet: beskriv hendelsen, ikke personen. */
export function PrivacyHint({ className }) {
  return (
    <p className={cn('flex items-start gap-1.5 text-[13px] leading-5 text-[var(--hr-muted)]', className)} data-testid="privacy-hint">
      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
      <span>Beskriv hendelsen, ikke personen. Unngå unødvendige personopplysninger og helseopplysninger.</span>
    </p>
  );
}

/* ---------- Skjemafelt (deles av «Meld avvik» og «Rediger») ---------- */

export const emptyDeviationForm = ({ kind = '', projectId = '', subprojectId = '' } = {}) => ({
  kind, title: '', date: today(), time: '', projectId, subprojectId, description: '', severity: 'middels', immediateAction: '',
  responsibleId: '', dueDate: '', reportedById: '', eventType: NOT_CLARIFIED, involved: NOT_CLARIFIED, workOperation: '', relatedTo: NOT_CLARIFIED, cause: NOT_CLARIFIED,
});

export function formFromDeviation(d) {
  return {
    kind: d.kind, title: d.title ?? '', date: d.date, time: d.time ?? '', projectId: d.projectId ?? '', subprojectId: d.subprojectId ?? '', description: d.description ?? '', severity: d.severity ?? 'middels',
    immediateAction: d.immediateAction ?? '', responsibleId: d.responsibleId ?? '', dueDate: d.dueDate ?? '', reportedById: d.reportedById ?? '',
    eventType: d.eventType ?? NOT_CLARIFIED, involved: d.involved ?? NOT_CLARIFIED, workOperation: d.workOperation ?? '', relatedTo: d.relatedTo ?? NOT_CLARIFIED, cause: d.cause ?? NOT_CLARIFIED,
  };
}

export function validateDeviationForm(f) {
  const e = {};
  if (!f.kind) e.kind = 'Velg RUH eller kvalitetsavvik';
  if ((f.title ?? '').trim().length < 3) e.title = 'Skriv en kort tittel (minst 3 tegn)';
  if (!f.date) e.date = 'Velg dato';
  if (!f.projectId) e.projectId = 'Velg prosjekt';
  if (f.dueDate && f.date && f.dueDate < f.date) e.dueDate = 'Fristen kan ikke være før hendelsesdatoen';
  return e;
}

/** Kategorier og ansvar – «Flere detaljer». Alt kan stå tomt / «Ikke avklart» og fylles ut senere i saksrommet. */
export function DeviationDetailFields({ form, set, refs, refsState, errors = {}, prefix = 'dev' }) {
  const isRuh = form.kind === 'ruh';
  return (
    <div className="space-y-4">
      {isRuh ? (
        <>
          <Field label="Type hendelse" htmlFor={`${prefix}-eventType`}>
            <CategorySelect id={`${prefix}-eventType`} value={form.eventType} onChange={set('eventType')} options={refs?.ruhEventTypes} testId={`${prefix}-eventType`} />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Hendelsen omfattet" htmlFor={`${prefix}-involved`}>
              <CategorySelect id={`${prefix}-involved`} value={form.involved} onChange={set('involved')} options={refs?.ruhInvolved} testId={`${prefix}-involved`} />
            </Field>
            <Field label="Arbeidsoperasjon" htmlFor={`${prefix}-workOperation`}>
              <SelectField id={`${prefix}-workOperation`} value={form.workOperation} onValueChange={set('workOperation')} allowEmpty emptyLabel="Ikke oppgitt" options={(refs?.workOperations ?? []).map((o) => ({ value: o.id, label: o.label }))} testId={`${prefix}-workOperation`} />
            </Field>
          </div>
        </>
      ) : (
        <Field label="Relatert til" htmlFor={`${prefix}-relatedTo`}>
          <CategorySelect id={`${prefix}-relatedTo`} value={form.relatedTo} onChange={set('relatedTo')} options={refs?.qualityRelated} testId={`${prefix}-relatedTo`} />
        </Field>
      )}
      <Field label="Årsak" htmlFor={`${prefix}-cause`} hint="«Ikke avklart» er et ærlig valg – årsaken kan settes når den er undersøkt.">
        <CategorySelect id={`${prefix}-cause`} value={form.cause} onChange={set('cause')} options={refs?.causes} testId={`${prefix}-cause`} />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Ansvarlig for oppfølging" htmlFor={`${prefix}-responsible`}>
          <PersonSelect id={`${prefix}-responsible`} value={form.responsibleId} onChange={set('responsibleId')} refs={refs} refsState={refsState} emptyLabel="Ikke tildelt ennå" testId={`${prefix}-responsible`} />
        </Field>
        <Field label="Frist" htmlFor={`${prefix}-due`} hint="Valgfritt" error={errors.dueDate}>
          <Input id={`${prefix}-due`} type="date" value={form.dueDate} min={form.date} onChange={(e) => set('dueDate')(e.target.value)} className={inputClass} data-testid={`${prefix}-due`} />
        </Field>
      </div>
      <Field label="Meldt av" htmlFor={`${prefix}-reporter`} hint="Fiktiv demomedarbeider. Ingen varsling sendes.">
        <PersonSelect id={`${prefix}-reporter`} value={form.reportedById} onChange={set('reportedById')} refs={refs} emptyLabel="Ikke oppgitt" testId={`${prefix}-reporter`} />
      </Field>
    </div>
  );
}

/* ---------- Meld avvik ---------- */

/** Laster opp vedlegg etter at avviket er opprettet – feil her stopper ikke saken. */
async function uploadAfterCreate(created, files) {
  if (!files.length) return created;
  try {
    return await uploadDeviationAttachments(created.id, files);
  } catch (err) {
    toast.error('Avviket er lagret, men vedlegg feilet', { description: errText(err) });
    return created;
  }
}

/**
 * «Meld avvik»: først RUH eller kvalitet, så det nødvendige (tittel, dato, prosjekt, alvorlighet), beskrivelse og strakstiltak.
 * Kategorier, ansvarlig og frist ligger under «Flere detaljer» og kan fylles ut senere i saksrommet.
 */
export function MeldAvvikSheet({ open, onOpenChange, initialKind = '', initialProjectId = '', initialSubprojectId = '', onSaved, returnTo }) {
  const refsState = useDriftRefs();
  const { refs } = refsState;
  const navigate = useNavigate();
  const [form, setForm] = useState(() => emptyDeviationForm({ kind: initialKind, projectId: initialProjectId, subprojectId: initialSubprojectId }));
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [more, setMore] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(emptyDeviationForm({ kind: initialKind, projectId: initialProjectId, subprojectId: initialSubprojectId }));
      setFiles([]);
      setErrors({});
      setMore(false);
    }
  }, [open, initialKind, initialProjectId, initialSubprojectId]);
  const set = (k) => (v) => {
    setForm((f) => (k === 'projectId' && v !== f.projectId ? { ...f, projectId: v, subprojectId: '' } : { ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };
  const isRuh = form.kind === 'ruh';

  const save = async () => {
    const e = validateDeviationForm(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      let created = await createDeviation({
        kind: form.kind,
        title: form.title.trim(),
        date: form.date,
        time: form.time || null,
        projectId: form.projectId,
        subprojectId: form.subprojectId || null,
        description: form.description.trim() || null,
        severity: form.severity,
        immediateAction: form.immediateAction.trim() || null,
        responsibleId: form.responsibleId || null,
        reportedById: form.reportedById || null,
        dueDate: form.dueDate || null,
        eventType: isRuh ? form.eventType || null : null,
        involved: isRuh ? form.involved || null : null,
        workOperation: isRuh ? form.workOperation || null : null,
        relatedTo: !isRuh ? form.relatedTo || null : null,
        cause: form.cause || null,
      });
      created = await uploadAfterCreate(created, files);
      toast.success(`${DEVIATION_KIND_LABELS[form.kind]} meldt`, { description: `${created.project?.name ?? ''} · Innmeldt${created.responsible ? ` · ansvarlig ${created.responsible.name}` : ''}. Ingen melding sendes (eksempeldata).` });
      onOpenChange(false);
      onSaved?.(created);
      navigate(deviationPath(created.id), { state: returnTo ? { from: returnTo } : undefined });
    } catch (err) {
      toast.error('Kunne ikke melde avviket', { description: errText(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Meld avvik"
      description="Det viktigste først. Kategorier, ansvarlig og frist kan fylles ut nå eller senere i saksrommet. Eksempeldata – ingen melding sendes."
      testId="deviation-sheet"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" className="h-11 rounded-[10px] lg:h-10" onClick={() => onOpenChange(false)} disabled={saving} data-testid="deviation-cancel">
            Avbryt
          </Button>
          <Button className={cn(primaryBtn, 'h-11 px-5 text-[14px] font-semibold lg:h-10')} onClick={save} disabled={saving} data-testid="deviation-save">
            {saving ? 'Lagrer …' : 'Meld avvik'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Hva slags avvik?" required error={errors.kind}>
          <KindPicker value={form.kind} onChange={set('kind')} testId="deviation-kind" />
        </Field>
        <Field label="Kort tittel" htmlFor="da-title" required error={errors.title} hint={isRuh ? 'F.eks. «Nestenulykke: dumper rygget mot lastebil ved kaikant»' : form.kind === 'kvalitet' ? 'F.eks. «Feil fraksjon levert til omfylling»' : 'Hva skjedde?'}>
          <Input id="da-title" value={form.title} onChange={(e) => set('title')(e.target.value)} maxLength={100} placeholder="Hva skjedde?" className={inputClass} data-testid="deviation-title" />
        </Field>
        <div className="grid grid-cols-[1fr_minmax(0,120px)] gap-3">
          <Field label="Dato" htmlFor="da-date" required error={errors.date}>
            <Input id="da-date" type="date" value={form.date} max={today()} onChange={(e) => set('date')(e.target.value)} className={inputClass} data-testid="deviation-date" />
          </Field>
          <Field label="Klokkeslett" htmlFor="da-time" hint="Valgfritt">
            <Input id="da-time" type="time" value={form.time} onChange={(e) => set('time')(e.target.value)} className={inputClass} data-testid="deviation-time" />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Prosjekt" htmlFor="da-project" required error={errors.projectId}>
            <ProjectSelect id="da-project" value={form.projectId} onChange={set('projectId')} refs={refs} testId="deviation-project" />
          </Field>
          <Field label="Underprosjekt" htmlFor="da-subproject" hint="Valgfritt">
            <SubprojectSelect id="da-subproject" value={form.subprojectId} onChange={set('subprojectId')} refs={refs} projectId={form.projectId} testId="deviation-subproject" />
          </Field>
        </div>
        <Field label="Alvorlighet" required>
          <SeverityPicker value={form.severity} onChange={set('severity')} testId="deviation-severity" />
        </Field>
        <Field label="Beskrivelse" htmlFor="da-desc" hint="Hva ble observert, hvor og hvordan? Kan utdypes senere.">
          <Textarea id="da-desc" value={form.description} onChange={(e) => set('description')(e.target.value)} rows={3} maxLength={4000} className={textareaClass} data-testid="deviation-description" />
          <PrivacyHint className="mt-1.5" />
        </Field>
        <Field label="Strakstiltak" htmlFor="da-immediate" hint="Hva ble gjort med en gang? Valgfritt.">
          <Textarea id="da-immediate" value={form.immediateAction} onChange={(e) => set('immediateAction')(e.target.value)} rows={2} maxLength={2000} className={cn(textareaClass, 'min-h-[64px]')} data-testid="deviation-immediate" />
        </Field>
        <Field label="Vedlegg" htmlFor="da-files">
          <FilePicker files={files} onChange={setFiles} testId="deviation-attachments" />
        </Field>

        <Collapsible open={more} onOpenChange={setMore}>
          <CollapsibleTrigger asChild>
            <button type="button" className="flex w-full items-center justify-between rounded-[10px] border border-[var(--hr-border)] bg-white px-3.5 py-2.5 text-left text-[14px] font-medium text-[var(--hr-heading)] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid="deviation-more-toggle" aria-expanded={more}>
              <span>
                Flere detaljer
                <span className="ml-1.5 font-normal text-[var(--hr-muted)]">kategorier, ansvarlig, frist</span>
              </span>
              <ChevronDown className={cn('h-4 w-4 text-[var(--hr-muted-2)] transition-transform duration-150', more && 'rotate-180')} aria-hidden="true" />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SheetSection title="Kategorier og ansvar" description="Alt kan stå som «Ikke avklart» eller tomt og settes senere i saksrommet." className="mt-3 border-t-0 pt-0">
              {form.kind ? <DeviationDetailFields form={form} set={set} refs={refs} refsState={refsState} errors={errors} prefix="deviation" /> : <p className="text-[14px] text-[var(--hr-muted)]">Velg RUH eller kvalitetsavvik først.</p>}
            </SheetSection>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </FormSheet>
  );
}

/* ---------- Lister ---------- */

export const DEVIATION_VIEWS = [
  { id: 'apne', label: 'Åpne', match: (d) => d.open },
  { id: 'forfalt', label: 'Forfalt', match: (d) => d.open && (d.overdue || d.actionsOverdue > 0) },
  { id: 'lukket', label: 'Lukket', match: (d) => !d.open },
  { id: 'alle', label: 'Alle', match: () => true },
];

export function DeviationViewChips({ value, onChange, deviations, testId = 'dev-view' }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Utvalg" data-testid={testId}>
      {DEVIATION_VIEWS.map((v) => {
        const n = deviations.filter(v.match).length;
        const on = value === v.id;
        return (
          <button key={v.id} type="button" role="tab" aria-selected={on} onClick={() => onChange(v.id)} className={cn('inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', on ? 'border-[var(--hr-heading)] bg-[var(--hr-heading)] text-white' : 'border-[var(--hr-border)] bg-white text-[var(--hr-text)] hover:bg-[var(--hr-surface-2)]')} data-testid={`${testId}-${v.id}`}>
            {v.label}
            <span className={cn('hr-tabular text-[12px]', on ? 'text-white/80' : 'text-[var(--hr-muted-2)]')}>{n}</span>
          </button>
        );
      })}
    </div>
  );
}

const listDate = (d) => (!d.open && d.closure?.date ? d.closure.date : d.date);
const listDateTitle = (d) => (!d.open && d.closure?.date ? `Lukket ${dateText(d.closure.date)} · hendelse ${dateText(d.date)}` : d.time ? `Kl. ${d.time}` : undefined);

/**
 * Avviksliste: tabell på større skjermer, kort på mobil (eller alltid kort med `compact` – brukes i smale paneler). Klikk åpner saksrommet.
 * `from` ({ label, to }) følger med som navigasjonstilstand, så saksrommet kan lenke tilbake til der brukeren kom fra.
 */
export function DeviationList({ deviations, showProject = true, emptyText = 'Ingen avvik.', testId = 'dev-list', limit, from, compact = false }) {
  const navigate = useNavigate();
  const rows = limit ? deviations.slice(0, limit) : deviations;
  const open = (d) => navigate(deviationPath(d.id), { state: from ? { from } : undefined });
  if (!deviations.length) return <p className="px-5 py-6 text-[14px] text-[var(--hr-muted)]" data-testid={`${testId}-empty`}>{emptyText}</p>;
  return (
    <>
      <ul className={cn('divide-y divide-[var(--hr-hairline)]', !compact && 'md:hidden')} data-testid={`${testId}-cards`}>
        {rows.map((d) => (
          <li key={d.id}>
            <button type="button" onClick={() => open(d)} className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]" data-testid={`${testId}-card-${d.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <DeviationKindMark kind={d.kind} />
                  <span className="hr-tabular text-[13px] text-[var(--hr-muted)]" title={listDateTitle(d)}>{dateText(listDate(d))}</span>
                </div>
                <div className="mt-1 text-[15px] font-semibold leading-5 text-[var(--hr-heading)]">{d.title}</div>
                {showProject && d.project && <div className="text-[13px] text-[var(--hr-muted)]">{d.project.name}{d.subproject ? ` · ${d.subproject.name}` : ''}</div>}
                <DeviationBadges d={d} className="mt-1.5" />
                <div className="mt-1 text-[13px] text-[var(--hr-muted)]">
                  {d.responsible ? `Ansvarlig ${d.responsible.name}` : 'Ikke tildelt'}
                  {d.dueDate && d.open ? ` · frist ${dateText(d.dueDate)}` : ''}
                  {d.actions?.length ? ` · tiltak ${d.actionsDone}/${d.actions.length}` : ''}
                </div>
              </div>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      {!compact && (
        <div className="hidden overflow-x-auto md:block">
          <Table data-testid={`${testId}-table`}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[1%] whitespace-nowrap">Dato</TableHead>
              <TableHead>Avvik</TableHead>
              {showProject && <TableHead className="hidden whitespace-nowrap xl:table-cell">Prosjekt</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead className="hidden whitespace-nowrap lg:table-cell">Ansvarlig</TableHead>
              <TableHead className="hidden whitespace-nowrap xl:table-cell">Frist</TableHead>
              <TableHead className="hidden whitespace-nowrap text-right lg:table-cell">Tiltak</TableHead>
              <TableHead className="w-[1%]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((d) => (
              <TableRow key={d.id} className="cursor-pointer" onClick={() => open(d)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && open(d)} data-testid={`${testId}-row-${d.id}`}>
                <TableCell className="hr-tabular whitespace-nowrap" title={listDateTitle(d)}>{dateText(listDate(d))}</TableCell>
                <TableCell>
                  <div className="max-w-[260px] lg:max-w-[320px] xl:max-w-[440px]">
                    <div className="flex min-w-0 items-center gap-2">
                      <DeviationKindMark kind={d.kind} />
                      <span className="min-w-0 truncate font-medium text-[var(--hr-heading)]">{d.title}</span>
                    </div>
                    {showProject && d.project && <div className="mt-0.5 truncate text-[13px] text-[var(--hr-muted)] xl:hidden">{d.project.name}{d.subproject ? ` · ${d.subproject.name}` : ''}</div>}
                    <div className={cn('mt-0.5 truncate text-[13px] text-[var(--hr-muted)]', showProject && d.project && 'hidden xl:block')}>
                      {d.categoryLabel}
                      {isClarified(d.cause) ? ` · ${d.causeLabel}` : ' · årsak ikke avklart'}
                    </div>
                  </div>
                </TableCell>
                {showProject && (
                  <TableCell className="hidden xl:table-cell">
                    {d.project ? (
                      <div className="max-w-[220px]">
                        <div className="truncate font-medium text-[var(--hr-heading)]">{d.project.name}</div>
                        {d.subproject && <div className="truncate text-[13px] text-[var(--hr-muted)]">{d.subproject.name}</div>}
                      </div>
                    ) : (
                      d.projectId
                    )}
                  </TableCell>
                )}
                <TableCell><DeviationBadges d={d} /></TableCell>
                <TableCell className="hidden whitespace-nowrap lg:table-cell">{d.responsible?.name ?? <span className="text-[var(--hr-amber)]">Ikke tildelt</span>}</TableCell>
                <TableCell className={cn('hr-tabular hidden whitespace-nowrap xl:table-cell', d.overdue && 'font-semibold text-[var(--hr-amber)]')}>{d.open && d.dueDate ? dateText(d.dueDate) : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
                <TableCell className={cn('hr-tabular hidden whitespace-nowrap text-right lg:table-cell', d.actionsOverdue && 'font-semibold text-[var(--hr-amber)]')}>{d.actions?.length ? `${d.actionsDone}/${d.actions.length}` : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
                <TableCell><ChevronRight className="h-4 w-4 text-[var(--hr-muted-2)]" aria-hidden="true" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </div>
      )}
      {limit && deviations.length > limit && <p className="px-5 py-2 text-[13px] text-[var(--hr-muted)]">Viser {limit} av {deviations.length}.</p>}
    </>
  );
}
